import { useState, useCallback, useMemo } from 'react';
import { api, getUserInfo, setUserInfo } from '../../api/client';

const STORAGE_KEY = 'pinnedMenuItems';

/** Read pinned items from localStorage */
const readFromStorage = (): string[] => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return [];
    const parsed = JSON.parse(stored);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

/** Write pinned items to localStorage */
const writeToStorage = (pins: string[]): void => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(pins));
};

/**
 * Hook for managing sidebar pinned shortcuts.
 * Triple-write pattern: React state -> localStorage -> API (fire-and-forget).
 * Follows the persistence pattern from GettingStartedBanner.
 */
export const useSidebarPins = () => {
  const [pinnedItems, setPinnedItems] = useState<string[]>(() => {
    // Initialize from user preferences first, then localStorage fallback
    const userInfo = getUserInfo();
    const fromPrefs = userInfo?.preferences?.pinnedMenuItems;
    if (Array.isArray(fromPrefs)) {
      writeToStorage(fromPrefs as string[]);
      return fromPrefs as string[];
    }
    return readFromStorage();
  });

  const isPinned = useCallback(
    (itemId: string) => pinnedItems.includes(itemId),
    [pinnedItems]
  );

  const togglePin = useCallback((itemId: string) => {
    setPinnedItems(prev => {
      const next = prev.includes(itemId)
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId];

      // Write to localStorage
      writeToStorage(next);

      // Persist in userInfo localStorage (so it survives page refresh with full user context)
      const stored = getUserInfo();
      if (stored) {
        setUserInfo({
          ...stored,
          preferences: { ...(stored.preferences || {}), pinnedMenuItems: next },
        });
      }

      // Fire-and-forget API call
      api.profile.update({
        preferences: { pinnedMenuItems: next },
      } as any).catch(() => {
        // Ignore - pins are persisted locally anyway
      });

      return next;
    });
  }, []);

  return useMemo(
    () => ({ pinnedItems, togglePin, isPinned }),
    [pinnedItems, togglePin, isPinned]
  );
};
